import React from 'react';
import Box from '@mui/material/Box';
import { Theme, Typography, useMediaQuery, useTheme } from '@mui/material';
import StepShell, { PrimaryBtn } from '../StepShell';
import { QuizAnswer } from '../types';

interface Props {
  state: Partial<QuizAnswer>;
  setState: (a: Partial<QuizAnswer>) => void;
  onNext: () => void;
  onBack?: (() => void) | null;
  stepIdx: number;
  total: number;
}

export const VALID_QUANTITIES = [6, 7, 8, 9, 10, 11, 12] as const;

export default function Quantity({
  state,
  setState,
  onNext,
  onBack,
  stepIdx,
  total,
}: Props) {
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );
  const theme = useTheme();
  const qty = state?.mealQuantity ?? null;
  const continueDisabled = qty === null;

  const pick = (n: number) => setState({ ...state, mealQuantity: n });

  return (
    <StepShell
      stepIdx={stepIdx}
      total={total}
      mobile={isSmallScreen}
      onBack={onBack}
      footer={
        <PrimaryBtn onClick={onNext} disabled={continueDisabled}>
          Continue
        </PrimaryBtn>
      }
    >
      <Box sx={{ pt: isSmallScreen ? '4px' : '8px', pb: '16px' }}>
        <Box
          component="h2"
          sx={{
            fontWeight: 600,
            fontSize: isSmallScreen ? 30 : 40,
            lineHeight: 1.08,
            letterSpacing: '-0.015em',
            m: 0,
            mb: '6px',
            color: '#041C2C',
            textWrap: 'balance',
          }}
        >
          How many meals would you like?
        </Box>
        <Typography
          variant={'subtitle1'}
          sx={{
            fontSize: isSmallScreen ? 15 : 17,
            m: 0,
            mb: isSmallScreen ? '22px' : '28px',
          }}
        >
          Pick a plan size.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: isSmallScreen
              ? 'repeat(2, 1fr)'
              : `repeat(${VALID_QUANTITIES.length}, 1fr)`,
            gap: isSmallScreen ? '10px' : '14px',
          }}
        >
          {VALID_QUANTITIES.map((n) => {
            const active = qty === n;
            return (
              <Box
                key={n}
                component="button"
                onClick={() => pick(n)}
                sx={{
                  cursor: 'pointer',
                  background: active ? theme.palette.primary.main : '#FFFFFF',
                  color: active ? theme.palette.text.primary : '#041C2C',
                  border: active
                    ? `2px solid ${theme.palette.primary.main}`
                    : '2px solid #E5E5EE',
                  borderRadius: '14px',
                  py: isSmallScreen ? '24px' : '32px',
                  px: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '4px',
                  transition: 'all 120ms cubic-bezier(0.2, 0.8, 0.2, 1)',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: isSmallScreen ? 38 : 50,
                    lineHeight: 1,
                  }}
                >
                  {n}
                </Box>
                <Box
                  component="span"
                  sx={{ fontSize: 13, fontWeight: 500, opacity: 0.85 }}
                >
                  meals
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </StepShell>
  );
}
